import React, { useEffect, useMemo, useState, useRef } from "react";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { toast } from "sonner";
import {
  Layers, Camera, Loader2, FileDown, Ruler, Package, AlertTriangle, Check, Info, FileText, X, LayoutGrid, Plus,
} from "lucide-react";
import { getAllProducts, analyzeSubstrate } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const num = (v) => (typeof v === "number" ? v : parseFloat(v) || 0);
const chf = (v) => "CHF " + (num(v)).toLocaleString("de-CH", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// product's default unit (bag/bucket) price
const unitPrice = (p) => {
  const bag = num(p.specs?.bag_weight_kg);
  return num(p.specs?.price_per_bag) || num(p.specs?.price_per_kg) * bag || 0;
};

// quantity + cost for a layer. perMm=true -> consumption is kg/m²/mm (massetti/autoliv./rasanti).
// Price is always per kg (priority); the per-bag price is derived (pk × bag weight).
// pipeEquivMm = equivalent mm of material displaced by heating pipes (base layer only).
const calcLayer = (p, area, thk, perMm, priceKg, pipeEquivMm = 0) => {
  if (!p || p.__other) return null;
  const a = num(area), t = num(thk);
  const cons = num(p.specs?.consumption_kg_per_m2);
  const effThk = perMm ? Math.max(0, t - num(pipeEquivMm)) : t;
  const kgM2 = perMm ? cons * effThk : cons;
  const totalKg = kgM2 * a;
  const bag = num(p.specs?.bag_weight_kg);
  const bags = bag > 0 ? Math.ceil(totalKg / bag) : 0;
  const billedKg = bag > 0 ? bags * bag : totalKg; // whole packages
  const residualKg = Math.max(0, billedKg - totalKg);
  const defKg = num(p.specs?.price_per_kg) || (bag > 0 ? unitPrice(p) / bag : 0);
  const pk = priceKg !== "" && priceKg != null ? num(priceKg) : defKg;
  const unit = pk * bag; // derived per-bag price (read-only)
  const subtotal = billedKg * pk; // billed on whole packages
  return { kgM2, totalKg, bag, bags, billedKg, residualKg, unit, pk, subtotal };
};

// traffic light vs datasheet thickness range
const light = (thk, p) => {
  const smin = num(p?.specs?.spessore_min_mm);
  const smax = num(p?.specs?.spessore_max_mm);
  if (!smin && !smax) return { c: "green", key: "stratNoConstraint", range: "" };
  const lo = smin || 0, hi = smax || Infinity;
  const range = `${lo}-${smax || "∞"} mm`;
  if (thk < lo * 0.9 || thk > hi * 1.1) return { c: "red", key: "stratOutRange", range };
  if (thk < lo || thk > hi) return { c: "yellow", key: "stratAtLimit", range };
  const margin = (hi === Infinity ? lo : hi - lo) * 0.1;
  if ((lo > 0 && thk <= lo + margin) || (hi < Infinity && thk >= hi - margin))
    return { c: "yellow", key: "stratNearLimit", range };
  return { c: "green", key: "stratCompatible", range };
};

const LIGHT_BG = { green: "bg-emerald-500", yellow: "bg-amber-400", red: "bg-red-500" };

const SYSTEMS = {
  nuovo: { label: "Sottofondo Nuovo", quota: 30, baseSub: "autolivellanti", precolla: false, primer: "Gisogrund", base: "FT Plan", colla: "Flexmörtel Light" },
  ristrutturazione: { label: "Ristrutturazione su Esistente", quota: 35, baseSub: "autolivellanti", precolla: true, primer: "Epoxigrund", base: "Periplan Extra", colla: "Carraflex" },
  altaquota: { label: "Alta Quota Disponibile", quota: 90, baseSub: "massetti", precolla: false, primer: "Gisogrund", base: "Novoment", colla: "Carrament" },
};

const LCOL = { primer: "#8b5cf6", base: "#f59e0b", precolla: "#8b5cf6", colla: "#34d399", tile: "#94a3b8" };

// Stratigraphy brand/systems. Add a new entry to extend (e.g. Parquet PCI/Sika/Klebag, Resine Sika)
// without touching the calculation/3D logic. `brand` filters products; `available:false` shows a disabled "coming soon" card.
const STRAT_SYSTEMS = [
  { id: "tiles_pci", labelKey: "stratSysTilesPci", brand: "pci", icon: LayoutGrid, accent: "#f59e0b", available: true },
  { id: "tiles_sika", labelKey: "stratSysTilesSika", brand: "sika", icon: LayoutGrid, accent: "#f6c500", available: true },
];
const systemBrand = (id) => (STRAT_SYSTEMS.find((s) => s.id === id) || STRAT_SYSTEMS[0]).brand;

// Synthetic "other material" entry: keeps the layer thickness but carries no cost.
const OTHER_ID = "__other__";
const makeOther = (name) => ({ id: OTHER_ID, name, brand: "", subcategory: "", description: "", specs: {}, __other: true });

// Large, touch-friendly button to toggle an optional layer on/off.
const AddLayerButton = ({ active, onToggle, labelAdd, labelRemove, testid }) => (
  <button
    type="button"
    data-testid={testid}
    onClick={onToggle}
    aria-pressed={active}
    className={`flex min-h-[52px] w-full items-center justify-center gap-2 rounded-md border-2 px-4 py-3 text-sm font-bold transition-colors ${
      active
        ? "border-primary bg-primary/10 text-foreground hover:bg-primary/20"
        : "border-dashed border-primary/70 text-primary hover:bg-primary/10"
    }`}
  >
    {active ? <Check className="h-5 w-5 shrink-0" /> : <Plus className="h-5 w-5 shrink-0" />}
    <span>{active ? labelRemove : labelAdd}</span>
  </button>
);

function LayerSelect({ layerKey, list, selectedId, thk, editableThk, onThk, calc, note, prodById, getPriceKg, setOverride, setActiveK, activeK, setSel, t }) {
  const p = prodById(selectedId);
  const thkN = num(thk);
  const isOther = !!p?.__other;
  const l = p && !isOther && (layerKey === "base" || layerKey === "colla") ? light(thkN, p) : null;
  const priceKg = getPriceKg(selectedId);
  const defKg = p ? (num(p.specs?.price_per_kg) || (num(p.specs?.bag_weight_kg) > 0 ? unitPrice(p) / num(p.specs?.bag_weight_kg) : 0)) : 0;
  const shownKg = priceKg === undefined || priceKg === null ? defKg : priceKg;
  const setKg = (value) => selectedId && setOverride((o) => ({ ...o, [selectedId]: value }));
  return (
    <div className="border border-border bg-card p-4 transition-colors" data-testid={`layer-${layerKey}`}
      onMouseEnter={() => setActiveK(layerKey)} onMouseLeave={() => setActiveK((k) => (k === layerKey ? null : k))}
      onFocusCapture={() => setActiveK(layerKey)} onBlurCapture={() => setActiveK((k) => (k === layerKey ? null : k))}
      style={activeK === layerKey ? { boxShadow: "inset 0 0 0 2px #f59e0b" } : undefined}>
      <div className="mb-3 flex items-center gap-2">
        <span className="font-display text-sm font-bold">{note}</span>
        {l && (
          <span className="ml-auto flex items-center gap-1.5 text-xs font-semibold">
            <span className={`h-2.5 w-2.5 rounded-full ${LIGHT_BG[l.c]}`} />
            <span className={l.c === "red" ? "text-red-500" : l.c === "yellow" ? "text-amber-500" : "text-emerald-500"}>{t(l.key)}{l.range ? ` (${l.range})` : ""}</span>
          </span>
        )}
      </div>

      <div className="flex flex-col gap-2 lg:flex-row lg:items-end">
        <label className="flex min-w-0 flex-1 flex-col gap-1">
          <span className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">{t("stratFieldProduct")}</span>
          <select data-testid={`select-${layerKey}`} value={selectedId || ""}
            onChange={(e) => setSel((s) => ({ ...s, [layerKey]: e.target.value }))}
            className="h-10 w-full border border-input bg-surface px-2 text-sm outline-none focus:ring-1 focus:ring-primary">
            {list.length === 0 && <option value="">{t("stratNoProduct")}</option>}
            {list.map((pp) => <option key={pp.id} value={pp.id}>{pp.name}</option>)}
          </select>
        </label>

        <label className="flex w-full flex-col gap-1 lg:w-[88px]">
          <span className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">{t("stratFieldThk")}</span>
          <div className={`flex h-10 items-center border border-input px-2 ${editableThk ? "bg-surface" : "bg-muted"}`}>
            <input data-testid={`thk-${layerKey}`} type="number" inputMode="decimal" value={thk ?? ""} disabled={!editableThk}
              onChange={(e) => onThk && onThk(e.target.value)} placeholder="0"
              className="w-full bg-transparent text-right font-mono text-sm outline-none disabled:text-muted-foreground" />
            <span className="ml-1 text-xs text-muted-foreground">mm</span>
          </div>
        </label>

        <label className="flex w-full flex-col gap-1 lg:w-[190px]">
          <span className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">{t("stratFieldPriceKg")}</span>
          <div className="flex h-10 items-stretch border border-input bg-surface">
            <span className="flex shrink-0 items-center bg-primary px-2 text-xs font-bold text-primary-foreground">CHF/kg</span>
            <input data-testid={`price-${layerKey}`} type="number" step="0.01" inputMode="decimal" value={isOther ? 0 : shownKg} disabled={isOther}
              onChange={(e) => setKg(e.target.value)}
              className="w-full bg-transparent px-2 text-right font-mono text-sm outline-none disabled:text-muted-foreground" />
          </div>
          <span className="text-right font-mono text-[10px] text-muted-foreground" data-testid={`unitprice-${layerKey}`}>
            ≈ {calc ? chf(calc.unit) : "—"}/{t("stratBag")} {calc?.bag ? `${calc.bag}kg` : ""}
          </span>
        </label>
      </div>

      {calc && (
        <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 border-t border-border pt-2 font-mono text-xs text-muted-foreground">
          <span>{calc.totalKg.toFixed(1)} {t("stratKgNeeded")}</span>
          <span className="flex items-center gap-1 font-semibold text-foreground"><Package className="h-3 w-3" /> {calc.bags || "—"} × {calc.bag}kg</span>
          {calc.residualKg > 0.05 && <span className="text-amber-500" data-testid={`residual-${layerKey}`}>{t("stratLeftover")} {calc.residualKg.toFixed(1)} kg</span>}
          <span className="text-muted-foreground/70">{chf(calc.pk)}/kg</span>
          <span className="ml-auto text-sm font-bold text-foreground">{chf(calc.subtotal)}</span>
        </div>
      )}
    </div>
  );
}

// Realistic material palette for the 3D stratigraphy (front/top/side faces + ink)
const MAT = {
  tile: { base: "#d7d0c2", top: "#ebe5da", side: "#b4ac9b", ink: "#2a2620" },
  colla: { base: "#b07d3e", top: "#c99a58", side: "#875d29", ink: "#fdf6ec" },
  base: { base: "#8f958f", top: "#a9afa8", side: "#6c726b", ink: "#f3f5f2" },
  precolla: { base: "#dfe3da", top: "#eef1e9", side: "#bcc1b4", ink: "#2d312a" },
  primer: { base: "#e7ebe0", top: "#f3f6ee", side: "#c6cabd", ink: "#2d312a" },
  pipe: { base: "#b9bdc0", top: "#cfd3d5", side: "#90979b", ink: "#1f2937" },
};
const TEX = {
  tile: { backgroundImage: "linear-gradient(120deg, rgba(255,255,255,.5), rgba(0,0,0,.05) 45%, rgba(255,255,255,.35) 60%, rgba(0,0,0,.04)), repeating-linear-gradient(55deg, transparent 0 16px, rgba(90,80,65,.10) 17px 18px)" },
  colla: { backgroundImage: "radial-gradient(rgba(0,0,0,.22) 1px, transparent 1.6px)", backgroundSize: "5px 5px" },
  base: { backgroundImage: "radial-gradient(rgba(255,255,255,.14) 1px, transparent 2px), radial-gradient(rgba(0,0,0,.10) 1px, transparent 2px)", backgroundSize: "8px 8px, 11px 11px" },
  precolla: { backgroundImage: "linear-gradient(100deg, rgba(255,255,255,.65), rgba(255,255,255,.08) 45%, rgba(255,255,255,.55))" },
  primer: { backgroundImage: "linear-gradient(100deg, rgba(255,255,255,.65), rgba(255,255,255,.08) 45%, rgba(255,255,255,.55))" },
  pipe: { backgroundImage: "repeating-linear-gradient(90deg, #e3b008 0 4px, #c99a08 4px 9px, transparent 9px 34px), linear-gradient(180deg, rgba(0,0,0,.12), rgba(255,255,255,.12))", backgroundSize: "auto, auto" },
};
const DEPTH = 20;

// Flat-fill palette for the SVG technical cross-section (front / top / side faces)
const FILL = {
  tile: { f: "#d7d0c2", top: "#ebe5da", side: "#b4ac9b" },
  colla: { f: "#b07d3e", top: "#c99a58", side: "#875d29" },
  precolla: { f: "#a78bfa", top: "#c4b5fd", side: "#7c3aed" },
  base: { f: "#9aa09a", top: "#b4bab2", side: "#767c75" },
  primer: { f: "#a78bfa", top: "#c4b5fd", side: "#7c3aed" },
  substrate: { f: "#8a8782", top: "#a3a09a", side: "#6d6b66" },
  heating: { f: "#e2e8f0", top: "#eef2f7", side: "#cbd2db" },
};

// The "base" (leveling) layer pools related subcategories so self-levelling skim
// compounds (rasanti) like 'PCI FT Plan' also appear under Autolivellante.
const BASE_GROUPS = { autolivellanti: ["autolivellanti", "rasanti"], massetti: ["massetti"] };
const baseSubs = (sub) => BASE_GROUPS[sub] || [sub];
// A product belongs to a subcategory either as primary OR via its multi-use `applications` list.
const hasSub = (p, s) => !!p && (p.subcategory === s || (Array.isArray(p.applications) && p.applications.includes(s)));
const inBase = (p, sub) => baseSubs(sub).some((s) => hasSub(p, s));

const pickByName = (list, sub) => {
  if (!list.length) return null;
  const f = list.find((p) => p.name.toLowerCase().includes((sub || "").toLowerCase()));
  return (f || list[0]).id;
};

export default function StratigraphyConfigurator() {
  const { t } = useApp();
  const [allProducts, setAllProducts] = useState([]);
  const [products, setProducts] = useState([]);
  const [brandSys, setBrandSys] = useState(STRAT_SYSTEMS[0].id);
  const sysBrandLabel = systemBrand(brandSys).toUpperCase();
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);
  const [hoverK, setHoverK] = useState(null);
  const [activeK, setActiveK] = useState(null);
  const [quota, setQuota] = useState(30);
  const [tile, setTile] = useState(6);
  const [area, setArea] = useState(20);
  const [collaThk, setCollaThk] = useState(10);
  const [primerThk, setPrimerThk] = useState(0);
  const [precollaOn, setPrecollaOn] = useState(false);
  const [primerOn, setPrimerOn] = useState(true);
  const [baseSub, setBaseSub] = useState("autolivellanti");
  const [sel, setSel] = useState({ primer: null, base: null, precolla: null, colla: null });
  const [override, setOverride] = useState({});
  const [sys, setSys] = useState("nuovo");
  const [heatingOn, setHeatingOn] = useState(false);
  const [pipeDia, setPipeDia] = useState(16);
  const [pipeSpacing, setPipeSpacing] = useState(100);
  const [photo, setPhoto] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [tileL, setTileL] = useState(60);
  const [tileW, setTileW] = useState(60);
  const [jointW, setJointW] = useState(3);
  const [groutId, setGroutId] = useState("");
  const [groutActive, setGroutActive] = useState(null);
  const fileRef = useRef(null);

  const cat = (s) => products.filter((p) => hasSub(p, s));
  const primers = useMemo(() => cat("primer"), [products]);
  const grouts = useMemo(() => cat("stucchi_fughe"), [products]);
  const bases = useMemo(() => products.filter((p) => inBase(p, baseSub)), [products, baseSub]);
  const collas = useMemo(() => cat("adesivi_standard"), [products]);

  const applySystem = (key, prods = products, bsOverride) => {
    const S = SYSTEMS[key];
    const bs = bsOverride || S.baseSub;
    setSys(key);
    setQuota(S.quota); setTile(6); setCollaThk(10); setPrimerThk(0);
    setPrecollaOn(S.precolla); setBaseSub(bs);
    const pc = (s) => prods.filter((p) => hasSub(p, s));
    setSel({
      primer: pickByName(pc("primer"), S.primer),
      base: pickByName(prods.filter((p) => inBase(p, bs)), S.base),
      precolla: pickByName(pc("primer"), "Gisogrund"),
      colla: pickByName(pc("adesivi_standard"), S.colla),
    });
  };

  const pickGrout = (prods) => {
    const g = prods.filter((p) => hasSub(p, "stucchi_fughe"));
    return (g.find((p) => /nanofug.*premium/i.test(p.name)) || g.find((p) => /nanofug/i.test(p.name)) || g[0])?.id || "";
  };

  // Re-pick products of the current brand for each layer, preserving quota/thickness/baseSub.
  const repickBrand = (prods) => {
    const S = SYSTEMS[sys];
    const pc = (s) => prods.filter((p) => hasSub(p, s));
    setSel({
      primer: pickByName(pc("primer"), S.primer),
      base: pickByName(prods.filter((p) => inBase(p, baseSub)), S.base),
      precolla: pickByName(pc("primer"), "Gisogrund"),
      colla: pickByName(pc("adesivi_standard"), S.colla),
    });
    setGroutId(pickGrout(prods));
  };

  useEffect(() => {
    getAllProducts()
      .then((all) => setAllProducts(all))
      .catch(() => setAllProducts([]))
      .finally(() => setLoading(false));
  }, []);

  // Load / reload brand products whenever the selected system changes.
  useEffect(() => {
    if (!allProducts.length) return;
    const brand = systemBrand(brandSys);
    const prods = allProducts.filter((p) => (p.brand || "").toLowerCase() === brand);
    setProducts(prods);
    repickBrand(prods);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brandSys, allProducts]);

  // when baseSub changes manually, re-pick a base product if current not in list
  useEffect(() => {
    if (!products.length) return;
    const list = products.filter((p) => inBase(p, baseSub));
    if (!list.find((p) => p.id === sel.base)) {
      setSel((s) => ({ ...s, base: list[0]?.id || null }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baseSub, products]);

  const baseThk = Math.max(0, num(quota) - num(tile) - num(collaThk) - num(primerThk));
  const invalid = num(quota) - num(tile) - num(collaThk) - num(primerThk) < 0;

  // heating pipes: equivalent mm of leveling material displaced per m²
  const pipeEquivMm = (() => {
    if (!heatingOn) return 0;
    const d = num(pipeDia) / 1000, s = num(pipeSpacing) / 1000;
    if (d <= 0 || s <= 0) return 0;
    const volPerM2 = (1 / s) * Math.PI * (d / 2) ** 2; // m³ per m²
    return volPerM2 * 1000; // -> mm equivalent
  })();

  const prodById = (id) => (id === OTHER_ID ? makeOther(t("stratOther")) : products.find((p) => p.id === id) || null);
  const getPriceKg = (id) => override[id];
  const pPrimer = primerOn ? prodById(sel.primer) : null;
  const pBase = prodById(sel.base);
  const pPrecolla = precollaOn ? prodById(sel.precolla) : null;
  const pColla = prodById(sel.colla);

  const cPrimer = primerOn ? calcLayer(pPrimer, area, primerThk, false, getPriceKg(sel.primer)) : null;
  const cBase = calcLayer(pBase, area, baseThk, true, getPriceKg(sel.base), pipeEquivMm);
  const cPrecolla = calcLayer(pPrecolla, area, 0, false, getPriceKg(sel.precolla));
  const cColla = calcLayer(pColla, area, collaThk, false, getPriceKg(sel.colla));

  const total = (cPrimer?.subtotal || 0) + (cBase?.subtotal || 0) + (precollaOn ? cPrecolla?.subtotal || 0 : 0) + (cColla?.subtotal || 0);

  const onPhoto = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const url = URL.createObjectURL(f);
    const reader = new FileReader();
    reader.onload = () => {
      const im = new Image();
      im.onload = () => setPhoto({ file: f, url, dataUrl: reader.result, w: im.naturalWidth || 1, h: im.naturalHeight || 1 });
      im.onerror = () => setPhoto({ file: f, url, dataUrl: reader.result, w: 1, h: 1 });
      im.src = reader.result;
    };
    reader.readAsDataURL(f);
    setAnalysis(null);
  };
  const removePhoto = () => { if (photo?.url) URL.revokeObjectURL(photo.url); setPhoto(null); setAnalysis(null); };

  const runAnalysis = async () => {
    if (!photo?.file) return;
    setAnalyzing(true);
    try {
      const res = await analyzeSubstrate(photo.file);
      setAnalysis(res);
      const match = pickByName(primers, res.primer_type);
      if (match) setSel((s) => ({ ...s, primer: match }));
      toast.success(t("stratAnalysisDone"));
    } catch (e) {
      toast.error(t("stratAnalysisFail"));
    } finally {
      setAnalyzing(false);
    }
  };

  const genPDF = async () => {
    const doc = new jsPDF();
    doc.setFillColor(15, 23, 42); doc.rect(0, 0, 210, 22, "F");
    doc.setTextColor(245, 158, 11); doc.setFont("helvetica", "bold"); doc.setFontSize(16);
    doc.text("METEROX", 14, 14);
    doc.setTextColor(255, 255, 255); doc.setFontSize(10);
    doc.text(t("stratPdfHeader").replace("{brand}", sysBrandLabel), 60, 14);
    doc.setTextColor(30, 30, 30); doc.setFontSize(11); doc.setFont("helvetica", "normal");
    doc.text(`${t("stratPdfQuota")}: ${quota} mm   |   ${t("stratPdfTile")}: ${tile} mm   |   ${t("stratPdfArea")}: ${area} m²`, 14, 32);

    const rows = [];
    const addRow = (label, p, thk, calc) => {
      if (!p) return;
      const l = ["primer", "precolla"].includes(label.key) ? { c: "green" } : light(thk, p);
      rows.push([
        label.name,
        `${p.brand} ${p.name}`,
        thk ? `${thk.toFixed(1)} mm` : "—",
        { green: t("stratCompatOK"), yellow: t("stratCompatLimit"), red: t("stratCompatOut") }[l.c],
        calc?.bags ? `${calc.bags} × ${calc.bag}kg${calc.residualKg > 0.05 ? ` (${t("stratLeftoverShort")} ${calc.residualKg.toFixed(1)}kg)` : ""}` : (calc ? `${calc.totalKg.toFixed(1)} kg` : "—"),
        calc ? chf(calc.subtotal) : "—",
      ]);
    };
    const groutInPdf = groutProd && gTotalKg > 0;
    if (groutInPdf) {
      const gCompat = { green: t("stratCompatOK"), yellow: t("stratCompatLimit"), red: t("stratCompatOut") }[gLight.c];
      const gQty = `${gKgM2.toFixed(2)} kg/m² · ${gBag > 0 ? `${gBags} × ${gBag}kg${gResidual > 0.05 ? ` (${t("stratLeftoverShort")} ${gResidual.toFixed(1)}kg)` : ""}` : `${gTotalKg.toFixed(1)} kg`}`;
      rows.push([
        `5 · ${t("stratGroutTitle")}`,
        `${groutProd.brand} ${groutProd.name}`,
        `${gJoint} mm`,
        gCompat,
        gQty,
        chf(gSubtotal),
      ]);
    }
    addRow({ key: "colla", name: `4 · ${t("stratColla")}` }, pColla, num(collaThk), cColla);
    if (precollaOn) addRow({ key: "precolla", name: `3 · ${t("stratPrecolla")}` }, pPrecolla, 0, cPrecolla);
    addRow({ key: "base", name: `2 · ${baseSub === "massetti" ? t("stratMassetto") : t("stratAutolivellante")}` }, pBase, baseThk, cBase);
    addRow({ key: "primer", name: `1 · ${t("stratLayerPrimer")}` }, pPrimer, num(primerThk), cPrimer);

    autoTable(doc, {
      startY: 38,
      head: [[t("stratColLayer"), t("stratColProduct"), t("stratColThk"), t("stratColCompat"), t("stratColQty"), t("stratColSubtotal")]],
      body: rows,
      styles: { fontSize: 9, cellPadding: 2.5 },
      headStyles: { fillColor: [15, 23, 42] },
      columnStyles: { 5: { halign: "right" } },
    });
    const y = doc.lastAutoTable.finalY + 8;
    doc.setFont("helvetica", "bold"); doc.setFontSize(13);
    doc.text(`${t("stratPdfTotal")}: ${chf(total + (groutInPdf ? gSubtotal : 0))}`, 14, y);
    doc.setFont("helvetica", "normal"); doc.setFontSize(8); doc.setTextColor(120, 120, 120);
    doc.text(t("stratPdfDisclaimer"), 14, y + 8);

    // exploded stratigraphy drawing (rasterised from the on-screen SVG)
    try {
      const svgEl = document.querySelector('[data-testid="stratigraphy-svg"]');
      if (svgEl) {
        const vb = svgEl.viewBox.baseVal;
        const clone = svgEl.cloneNode(true);
        clone.setAttribute("width", vb.width);
        clone.setAttribute("height", vb.height);
        clone.setAttribute("xmlns", "http://www.w3.org/2000/svg");
        const xml = new XMLSerializer().serializeToString(clone);
        const url = "data:image/svg+xml;base64," + btoa(unescape(encodeURIComponent(xml)));
        const dataUrl = await new Promise((resolve, reject) => {
          const img = new Image();
          img.onload = () => {
            const scale = 2;
            const cv = document.createElement("canvas");
            cv.width = vb.width * scale; cv.height = vb.height * scale;
            const ctx = cv.getContext("2d");
            ctx.scale(scale, scale);
            ctx.fillStyle = "#ffffff";
            ctx.fillRect(0, 0, vb.width, vb.height);
            ctx.drawImage(img, 0, 0);
            resolve(cv.toDataURL("image/jpeg", 0.9));
          };
          img.onerror = reject;
          img.src = url;
        });
        const imgW = 95, imgH = imgW * (vb.height / vb.width);
        let iy = y + 16;
        if (iy + imgH + 6 > 290) { doc.addPage(); iy = 20; }
        doc.setFont("helvetica", "bold"); doc.setFontSize(11); doc.setTextColor(30, 30, 30);
        doc.text(t("stratViewTitle"), 14, iy);
        doc.addImage(dataUrl, "JPEG", (210 - imgW) / 2, iy + 4, imgW, imgH);
      }
    } catch (e) { /* skip drawing if rasterisation fails */ }

    doc.save("sistema-pavimento-meterox.pdf");
  };

  const datasheetRows = (p) => {
    const s = p.specs || {};
    const rows = [];
    const push = (label, val) => { if (val !== undefined && val !== null && val !== "") rows.push([label, String(val)]); };
    const perMm = ["autolivellanti", "rasanti", "massetti"].includes(p.subcategory);
    push(t("stratDsCategory"), (p.subcategory || "").replace(/_/g, " "));
    push(t("stratDsResistance"), s.resistenza);
    push(t("stratDsConsumption"), s.consumption_kg_per_m2 ? `${s.consumption_kg_per_m2} kg/m²${perMm ? "/mm" : ""}` : null);
    push(t("stratDsYield"), s.yield_m2_per_bag ? `${s.yield_m2_per_bag} m²/${t("stratBag")}` : null);
    push(t("stratDsPackage"), s.bag_weight_kg ? `${s.bag_weight_kg} kg` : null);
    push(t("stratDsThickness"), (s.spessore_min_mm || s.spessore_max_mm) ? `${s.spessore_min_mm || 0}–${s.spessore_max_mm || "∞"} mm` : null);
    push(t("stratDsWorkability"), s.lavorabilita_min ? `${s.lavorabilita_min} min` : null);
    push(t("stratDsWalkable"), s.pedonabile_ore ? `${s.pedonabile_ore} h` : null);
    push(t("stratDsPriceKg"), s.price_per_kg ? `CHF ${s.price_per_kg}` : null);
    push(t("stratDsColor"), s.colore);
    push(t("stratDsCerts"), Array.isArray(s.certificazioni) ? s.certificazioni.join(", ") : s.certificazioni);
    const flags = [];
    if (s.rapida) flags.push(t("stratFlagRapid"));
    if (s.interno) flags.push(t("stratFlagInterior"));
    if (s.esterno) flags.push(t("stratFlagExterior"));
    if (flags.length) rows.push([t("stratDsFeatures"), flags.join(" · ")]);
    return rows;
  };

  if (loading) return <div className="flex justify-center py-24"><Loader2 className="h-7 w-7 animate-spin text-primary" /></div>;

  // grout (malte fuganti) calc
  const groutProd = groutId === OTHER_ID ? makeOther(t("stratOther")) : products.find((p) => p.id === groutId) || null;
  const gs = groutProd?.specs || {};
  const gLmm = num(tileL) * 10, gWmm = num(tileW) * 10, gJoint = num(jointW), gThk = num(tile);
  const gDens = num(gs.densita_kg_dm3) || 1.6;
  const gKgM2 = gLmm > 0 && gWmm > 0 ? ((gLmm + gWmm) / (gLmm * gWmm)) * gJoint * gThk * gDens : 0;
  const gTotalKg = gKgM2 * num(area);
  const gBag = num(gs.bag_weight_kg);
  const gBags = gBag > 0 ? Math.ceil(gTotalKg / gBag) : 0;
  const gBilled = gBag > 0 ? gBags * gBag : gTotalKg;
  const gResidual = Math.max(0, gBilled - gTotalKg);
  const gIsOther = !!groutProd?.__other;
  const gDefPk = num(gs.price_per_kg) || (gBag > 0 ? unitPrice(groutProd || { specs: {} }) / gBag : 0);
  const gPkOv = override[groutId];
  const gShownKg = gPkOv === undefined || gPkOv === null ? gDefPk : gPkOv;
  const gPk = gPkOv !== "" && gPkOv != null ? num(gPkOv) : gDefPk;
  const gSubtotal = gIsOther ? 0 : gBilled * gPk;
  const gLight = (() => {
    const lo = num(gs.larghezza_fuga_min_mm), hi = num(gs.larghezza_fuga_max_mm);
    if (!lo && !hi) return { c: "green", key: "stratNoConstraint", range: "" };
    const range = `${lo || 0}-${hi || "∞"} mm`;
    if (gJoint < lo || (hi && gJoint > hi)) return { c: "red", key: "stratOutRange", range };
    const margin = ((hi || lo) - lo) * 0.15;
    if ((lo && gJoint <= lo + margin) || (hi && gJoint >= hi - margin)) return { c: "yellow", key: "stratNearLimit", range };
    return { c: "green", key: "stratCompatible", range };
  })();
  const groutInTotal = groutProd && gTotalKg > 0;
  const grandTotal = total + (groutInTotal ? gSubtotal : 0);

  // exploded isometric stratigraphy geometry (top -> bottom)
  const totalMm = num(quota) || 1;
  const ISO = { c: 1.05, s: 0.2, W: 110, Dp: 110, GAP: 1, cx: 300 };
  const SVG_W = 440;
  const DIAMH = (ISO.W + ISO.Dp) * ISO.s; // vertical screen height of a top diamond
  const thkPx = (mm, min = 12, max = 52) => Math.min(max, Math.max(min, num(mm) * 1.3));
  const P = (x, y, topY, drop = 0) => [ISO.cx + (x - y) * ISO.c, topY + (x + y) * ISO.s + drop];
  const pstr = (arr) => arr.map((p) => p.join(",")).join(" ");

  const layerDefs = [
    { k: "tile", mm: num(tile), label: t("stratLayerTile"), prod: null, calc: null },
    { k: "colla", mm: num(collaThk), label: t("stratLayerColla"), prod: pColla, calc: cColla },
    ...(precollaOn ? [{ k: "precolla", mm: 2, label: t("stratPrecolla"), prod: pPrecolla, calc: cPrecolla }] : []),
    { k: "base", mm: baseThk, label: baseSub === "massetti" ? t("stratMassetto") : t("stratAutolivellante"), prod: pBase, calc: cBase },
    ...(heatingOn ? [{ k: "heating", mm: num(pipeDia), label: `${t("stratLayerSerpentine")} Ø${num(pipeDia)} · i${num(pipeSpacing)}`, prod: null, calc: null, heating: true }] : []),
    ...(primerOn ? [{ k: "primer", mm: num(primerThk), label: t("stratLayerPrimer"), prod: pPrimer, calc: cPrimer }] : []),
    { k: "substrate", mm: 30, label: t("stratLayerSubstrate"), prod: null, calc: null, fixed: true },
  ];

  let topY = 16;
  const iso = layerDefs.map((l) => {
    const h = l.heating ? thkPx(l.mm, 12, 26) : (l.k === "primer" || l.k === "precolla") ? thkPx(l.mm, 5, 16) : thkPx(l.mm);
    const A = P(0, 0, topY), B = P(ISO.W, 0, topY), C = P(ISO.W, ISO.Dp, topY), D = P(0, ISO.Dp, topY);
    const Bl = [B[0], B[1] + h], Cl = [C[0], C[1] + h], Dl = [D[0], D[1] + h];
    const o = {
      ...l, h, topY, A, B, C, D,
      top: pstr([A, B, C, D]),
      right: pstr([B, C, Cl, Bl]),
      left: pstr([D, C, Cl, Dl]),
      leftVertex: D, bottomY: C[1] + h,
    };
    topY += DIAMH + h + ISO.GAP;
    return o;
  });
  const SVG_H = topY + 14;
  const LABEL_X = ISO.cx - ISO.Dp * ISO.c; // constant screen x of every left vertex

  // serpentine pipe path (projected on the heating layer top face)
  const heatPath = (() => {
    const hl = iso.find((l) => l.heating);
    if (!hl) return null;
    const pad = 20, rows = 4;
    const ystep = (ISO.Dp - 2 * pad) / (rows - 1);
    const pts = [];
    for (let r = 0; r < rows; r++) {
      const y = pad + r * ystep;
      const xa = r % 2 === 0 ? pad : ISO.W - pad;
      const xb = r % 2 === 0 ? ISO.W - pad : pad;
      pts.push(P(xa, y, hl.topY), P(xb, y, hl.topY));
    }
    const mesh = [];
    for (let g = 0; g <= 4; g++) {
      const t = (g / 4) * ISO.W;
      mesh.push([P(t, 0, hl.topY), P(t, ISO.Dp, hl.topY)]);
      mesh.push([P(0, t, hl.topY), P(ISO.W, t, hl.topY)]);
    }
    return { d: "M " + pts.map((p) => p.join(",")).join(" L "), tube: Math.max(6, thkPx(hl.mm) * 0.7), mesh };
  })();

  const layerCtx = { prodById, getPriceKg, setOverride, setActiveK, activeK, setSel, t };


  return (
    <div data-testid="stratigraphy-configurator" className="mx-auto max-w-7xl">
      <div className="mb-6 flex items-center gap-3">
        <Layers className="h-8 w-8 text-primary" />
        <div>
          <h1 className="font-display text-3xl font-black tracking-tight sm:text-4xl">{t("stratTitle")}</h1>
          <p className="text-sm text-muted-foreground">{t("stratSubtitle").replace("{brand}", sysBrandLabel)}</p>
        </div>
      </div>

      {/* System selector — prominent, extensible */}
      <div className="mb-6" data-testid="system-selector">
        <p className="label-caps mb-2 text-muted-foreground">{t("stratSysTitle")}</p>
        <div className="flex flex-wrap gap-3">
          {STRAT_SYSTEMS.map((s) => {
            const Icon = s.icon;
            const active = brandSys === s.id;
            return (
              <button key={s.id} type="button" data-testid={`system-${s.id}`} disabled={s.available === false}
                onClick={() => s.available !== false && setBrandSys(s.id)}
                aria-pressed={active}
                className={`group relative flex min-w-[180px] flex-1 items-center gap-3 border-2 p-4 text-left transition-all duration-200 sm:flex-none disabled:cursor-not-allowed disabled:opacity-40 ${active ? "bg-card shadow-lg" : "border-border bg-card/40 hover:-translate-y-0.5 hover:bg-card hover:shadow-md"}`}
                style={active ? { borderColor: s.accent, boxShadow: `0 8px 24px -12px ${s.accent}` } : undefined}>
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md transition-colors"
                  style={{ background: active ? s.accent : "var(--surface, rgba(255,255,255,.05))", color: active ? "#1a1a1a" : s.accent }}>
                  <Icon className="h-6 w-6" />
                </span>
                <span className="min-w-0">
                  <span className="block font-display text-base font-black leading-tight">{t(s.labelKey)}</span>
                  <span className="block text-xs uppercase tracking-wide text-muted-foreground">{s.brand}</span>
                </span>
                {active && <span className="absolute -right-2 -top-2 flex h-5 w-5 items-center justify-center rounded-full shadow-md ring-2 ring-background" style={{ background: s.accent }}><Check className="h-3.5 w-3.5 text-[#1a1a1a]" /></span>}
              </button>
            );
          })}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1fr_minmax(340px,460px)] lg:items-start" data-testid="strat-layout">
      {/* LEFT top: parametri */}
      <div className="space-y-4 lg:col-start-1 lg:row-start-1">
      {/* inputs */}
      <div className="grid grid-cols-2 items-stretch gap-3 sm:grid-cols-4" data-testid="configurator-inputs">
        {[[t("stratQuotaTot"), quota, setQuota, "input-quota", "__all__"], [t("stratTileThk"), tile, setTile, "input-tile", "tile"], [t("stratArea"), area, setArea, "input-area", null]].map(([lbl, val, set, tid, hk]) => (
          <label key={tid} className="flex flex-col gap-1 border border-border bg-card p-3">
            <span className="label-caps min-h-[30px] leading-tight text-muted-foreground">{lbl}</span>
            <input data-testid={tid} type="number" inputMode="decimal" value={val} onChange={(e) => set(e.target.value)}
              onFocus={() => hk && setActiveK(hk)} onBlur={() => setActiveK((k) => (k === hk ? null : k))}
              className="mt-auto border border-input bg-surface px-2 py-2 font-mono text-lg outline-none focus:ring-1 focus:ring-primary" />
          </label>
        ))}
        <label className="flex flex-col gap-1 border border-border bg-card p-3">
          <span className="label-caps min-h-[30px] leading-tight text-muted-foreground">{t("stratBase")}</span>
          <select data-testid="input-basesub" value={baseSub} onChange={(e) => setBaseSub(e.target.value)}
            onFocus={() => setActiveK("base")} onBlur={() => setActiveK((k) => (k === "base" ? null : k))}
            className="mt-auto border border-input bg-surface px-2 py-[11px] text-sm outline-none focus:ring-1 focus:ring-primary">
            <option value="autolivellanti">{t("stratAutolivellante")}</option>
            <option value="massetti">{t("stratMassetto")}</option>
          </select>
        </label>
      </div>

      {invalid && (
        <div className="flex items-center gap-2 border border-red-500 bg-red-500/10 px-4 py-3 text-sm text-red-500" data-testid="invalid-quota">
          <AlertTriangle className="h-4 w-4" /> {t("stratInvalid")}
        </div>
      )}
      </div>

      {/* RIGHT: disegno tecnico (vista esplosa) */}
      <div className="border border-border bg-gradient-to-b from-surface/60 to-surface/10 p-4 sm:p-6 lg:col-start-2 lg:row-start-1 lg:row-span-2 lg:sticky lg:top-20 lg:max-h-[calc(100vh-6rem)] lg:overflow-hidden" data-testid="stratigraphy-visual">
        <div className="mb-3 flex items-center justify-between label-caps text-muted-foreground">
          <span><Ruler className="mr-1 inline h-3.5 w-3.5" />{t("stratViewTitle")}</span>
          <span className="font-mono">{t("stratQuota")} {totalMm.toFixed(0)} mm</span>
        </div>
        <div className="flex w-full justify-center">
          <svg viewBox={`0 0 ${SVG_W} ${SVG_H}`} width="100%" className="select-none text-foreground" style={{ maxHeight: "calc(100vh - 13rem)" }} data-testid="stratigraphy-svg">
            <defs>
              <pattern id="grout" width="30" height="30" patternUnits="userSpaceOnUse" patternTransform="skewX(-28) scale(1,0.52)">
                <path d="M0 0H30M0 0V30" stroke="rgba(0,0,0,.26)" strokeWidth="1.4" />
              </pattern>
              <pattern id="comb" width="22" height="7" patternUnits="userSpaceOnUse" patternTransform="rotate(13)">
                <rect y="0" width="22" height="3" fill="rgba(0,0,0,.24)" />
                <rect y="3" width="22" height="4" fill="rgba(255,255,255,.16)" />
              </pattern>
              <pattern id="combEdge" width="7" height="24" patternUnits="userSpaceOnUse">
                <rect x="0" width="3" height="24" fill="rgba(0,0,0,.2)" />
                <rect x="3" width="4" height="24" fill="rgba(255,255,255,.1)" />
              </pattern>
              <pattern id="speckle" width="8" height="8" patternUnits="userSpaceOnUse">
                <circle cx="2" cy="2" r="1" fill="rgba(0,0,0,.14)" />
                <circle cx="6" cy="6" r="1.1" fill="rgba(255,255,255,.22)" />
              </pattern>
              <pattern id="aggregate" width="11" height="11" patternUnits="userSpaceOnUse">
                <circle cx="3" cy="4" r="2" fill="rgba(0,0,0,.22)" />
                <circle cx="8.5" cy="8" r="2.4" fill="rgba(0,0,0,.16)" />
                <circle cx="8" cy="3" r="1.3" fill="rgba(255,255,255,.2)" />
              </pattern>
              <filter id="softshadow" x="-20%" y="-30%" width="140%" height="180%">
                <feGaussianBlur in="SourceGraphic" stdDeviation="5" />
              </filter>
            </defs>

            {/* slabs, drawn back-to-front (bottom layer first) */}
            {iso.slice().reverse().map((l) => {
              const c = FILL[l.k] || FILL.base;
              const clickProd = l.k === "tile" ? groutProd : l.prod;
              const clickable = !!clickProd && !clickProd.__other;
              const hov = hoverK === l.k;
              const act = activeK === l.k || (activeK === "__all__" && l.k !== "substrate");
              const hl = hov || act;
              return (
                <g key={l.k} data-testid={`iso-layer-${l.k}`}
                  onMouseEnter={() => setHoverK(l.k)}
                  onMouseLeave={() => setHoverK((k) => (k === l.k ? null : k))}
                  onClick={() => clickable && setDetail(clickProd)}
                  style={{ cursor: clickable ? "pointer" : "default", transform: hl ? "translate(6px,-9px)" : "none", transition: "transform .25s ease-out" }}>
                  <ellipse cx={ISO.cx} cy={l.bottomY + 7} rx={(ISO.W + ISO.Dp) * ISO.c * 0.38} ry={DIAMH * 0.26} fill="rgba(0,0,0,.13)" filter="url(#softshadow)" />
                  {l.heating ? (
                    <>
                      <polygon points={l.top} fill="rgba(226,232,240,.35)" stroke="rgba(0,0,0,.18)" strokeWidth="1" />
                      {heatPath && heatPath.mesh.map((m, mi) => (
                        <line key={mi} x1={m[0][0]} y1={m[0][1]} x2={m[1][0]} y2={m[1][1]} stroke="#94a3b8" strokeWidth="0.8" strokeOpacity="0.7" />
                      ))}
                      {heatPath && (
                        <>
                          <path d={heatPath.d} fill="none" stroke="#a16207" strokeWidth={heatPath.tube + 3} strokeLinejoin="round" strokeLinecap="round" />
                          <path d={heatPath.d} fill="none" stroke="#eab308" strokeWidth={heatPath.tube} strokeLinejoin="round" strokeLinecap="round" />
                          <path d={heatPath.d} fill="none" stroke="#fde68a" strokeWidth={heatPath.tube * 0.35} strokeLinejoin="round" strokeLinecap="round" strokeOpacity="0.85" />
                        </>
                      )}
                    </>
                  ) : (
                    <>
                      <polygon points={l.left} fill={c.f} stroke="rgba(0,0,0,.28)" strokeWidth="1" />
                      <polygon points={l.right} fill={c.side} stroke="rgba(0,0,0,.28)" strokeWidth="1" />
                      <polygon points={l.top} fill={c.top} stroke="rgba(0,0,0,.25)" strokeWidth="1" />
                      {l.k === "tile" && (() => {
                        const n = 3;
                        const cs = ISO.W / n, csD = ISO.Dp / n;
                        const g = Math.min(6, Math.max(1.4, gJoint * 0.9));
                        const jointYellow = groutActive === "joint";
                        const tileYellow = groutActive === "tile";
                        const cells = [];
                        for (let i = 0; i < n; i++) for (let j = 0; j < n; j++) {
                          const x0 = i === 0 ? 0 : i * cs + g / 2;
                          const x1 = i === n - 1 ? ISO.W : (i + 1) * cs - g / 2;
                          const y0 = j === 0 ? 0 : j * csD + g / 2;
                          const y1 = j === n - 1 ? ISO.Dp : (j + 1) * csD - g / 2;
                          const p = pstr([
                            P(x0, y0, l.topY),
                            P(x1, y0, l.topY),
                            P(x1, y1, l.topY),
                            P(x0, y1, l.topY),
                          ]);
                          cells.push(<polygon key={`c${i}-${j}`} points={p} fill={tileYellow ? "#fde68a" : c.top} stroke="rgba(0,0,0,.2)" strokeWidth="0.8" style={{ transition: "fill .2s" }} />);
                        }
                        return (
                          <>
                            <polygon points={l.top} fill={jointYellow ? "#facc15" : "#b7bcac"} style={{ transition: "fill .2s" }} data-testid="iso-grout-joint" />
                            {cells}
                          </>
                        );
                      })()}
                      {(l.k === "base" || l.k === "primer" || l.k === "precolla") && <polygon points={l.top} fill="url(#speckle)" />}
                      {l.k === "substrate" && photo && (
                        <image href={photo.dataUrl} width={photo.w || 1} height={photo.h || 1} preserveAspectRatio="xMidYMid slice"
                          transform={`matrix(${(l.B[0] - l.A[0]) / (photo.w || 1)},${(l.B[1] - l.A[1]) / (photo.w || 1)},${(l.D[0] - l.A[0]) / (photo.h || 1)},${(l.D[1] - l.A[1]) / (photo.h || 1)},${l.A[0]},${l.A[1]})`}
                          opacity="1" data-testid="iso-substrate-photo" />
                      )}
                      {l.k === "substrate" && !photo && <polygon points={l.top} fill="url(#aggregate)" />}
                      {l.k === "colla" && (
                        <>
                          <polygon points={l.left} fill="url(#combEdge)" />
                          <polygon points={l.right} fill="url(#combEdge)" />
                          <polygon points={l.top} fill="url(#comb)" />
                        </>
                      )}
                    </>
                  )}
                  {hl && !(l.k === "tile" && groutActive) && <polygon points={l.top} fill="rgba(245,158,11,.18)" stroke="#f59e0b" strokeWidth="2" />}
                </g>
              );
            })}

            {/* labels + horizontal leader lines */}
            {iso.map((l) => {
              const ly = l.leftVertex[1];
              const clickProd = l.k === "tile" ? groutProd : l.prod;
              const nLines = 2 + (clickProd ? 1 : 0);
              const firstDy = `${(-((nLines - 1) * 1.25) / 2) + 0.32}em`;
              const clickable = !!clickProd && !clickProd.__other;
              const hov = hoverK === l.k || activeK === l.k || (activeK === "__all__" && l.k !== "substrate");
              return (
                <g key={`lbl-${l.k}`} data-testid={`iso-label-${l.k}`}
                  onMouseEnter={() => clickable && setHoverK(l.k)}
                  onMouseLeave={() => setHoverK((k) => (k === l.k ? null : k))}
                  onClick={() => clickable && setDetail(clickProd)}
                  style={{ cursor: clickable ? "pointer" : "default" }}>
                  <line x1={LABEL_X - 26} y1={ly} x2={l.leftVertex[0]} y2={ly} stroke="currentColor" strokeOpacity="0.45" strokeWidth="1" />
                  <circle cx={l.leftVertex[0]} cy={ly} r="2.3" fill="currentColor" fillOpacity="0.5" />
                  <text x={LABEL_X - 32} y={ly} textAnchor="end" fill={hov ? "#f59e0b" : "currentColor"}>
                    <tspan x={LABEL_X - 32} dy={firstDy} style={{ fontSize: 13, fontWeight: 700 }}>{l.label}</tspan>
                    {clickProd && <tspan x={LABEL_X - 32} dy="1.25em" fillOpacity="0.6" style={{ fontSize: 11 }}>{(clickProd.name || "").slice(0, 24)}</tspan>}
                    <tspan x={LABEL_X - 32} dy="1.25em" fillOpacity="0.6" style={{ fontSize: 11, fontFamily: "monospace" }}>
                      {l.fixed ? "—" : num(l.mm) ? `${num(l.mm).toFixed(num(l.mm) < 1 ? 1 : 0)} mm` : "~0"}{l.calc ? ` · ${chf(l.calc.subtotal)}` : (l.k === "tile" && groutInTotal ? ` · ${chf(gSubtotal)}` : "")}
                    </tspan>
                  </text>
                </g>
              );
            })}
          </svg>
        </div>
        <p className="mt-2 flex items-center justify-center gap-1.5 text-center text-[10px] text-muted-foreground" data-testid="strat-hint">
          <FileText className="h-3 w-3" /> {t("stratHint")}
        </p>
        {heatingOn && pipeEquivMm > 0 && (
          <div className="mt-3 text-center text-xs font-semibold text-emerald-600" data-testid="pipe-saving">
            {t("stratPipeSaving").replace("{dia}", num(pipeDia)).replace("{spacing}", num(pipeSpacing)).replace("{eq}", pipeEquivMm.toFixed(1))}
          </div>
        )}
      </div>

      {/* LEFT bottom: strati e prezzi */}
      <div className="grid grid-cols-1 gap-3 lg:col-start-1 lg:row-start-2">
        {/* 0 · Piastrella & Fughe (malta fugante) */}
        <div className="border border-border bg-card p-4 transition-colors" data-testid="tile-grout-panel"
          onMouseEnter={() => setActiveK("tile")} onMouseLeave={() => setActiveK((k) => (k === "tile" ? null : k))}
          style={activeK === "tile" ? { boxShadow: "inset 0 0 0 2px #f59e0b" } : undefined}>
          <div className="mb-3 flex items-center gap-2">
            <span className="font-display text-sm font-bold">0 · {t("stratLayerTile")} & {t("stratGroutTitle")}</span>
            {groutProd && (
              <span className="ml-auto flex items-center gap-1.5 text-xs font-semibold">
                <span className={`h-2.5 w-2.5 rounded-full ${LIGHT_BG[gLight.c]}`} />
                <span className={gLight.c === "red" ? "text-red-500" : gLight.c === "yellow" ? "text-amber-500" : "text-emerald-500"}>{t(gLight.key)}{gLight.range ? ` (${gLight.range})` : ""}</span>
              </span>
            )}
          </div>
          <p className="mb-3 text-[11px] text-muted-foreground">{t("stratGroutReuse")} · {t("stratTileThk")}: {num(tile)} mm · {t("stratArea")}: {num(area)} m²</p>
          <div className="grid grid-cols-3 gap-2" data-testid="grout-inputs">
            {[[t("stratGroutTileL"), tileL, setTileL, "grout-tilel", "tile"], [t("stratGroutTileW"), tileW, setTileW, "grout-tilew", "tile"], [t("stratGroutJoint"), jointW, setJointW, "grout-joint", "joint"]].map(([lbl, val, set, tid, hk]) => (
              <label key={tid} className="flex flex-col gap-1">
                <span className="text-[10px] font-semibold uppercase leading-tight tracking-wide text-muted-foreground">{lbl}</span>
                <input data-testid={tid} type="number" inputMode="decimal" value={val} onChange={(e) => set(e.target.value)}
                  onFocus={() => setGroutActive(hk)} onBlur={() => setGroutActive((k) => (k === hk ? null : k))}
                  className="h-10 border border-input bg-surface px-2 text-right font-mono text-sm outline-none focus:ring-1 focus:ring-primary" />
              </label>
            ))}
          </div>
          <div className="mt-3 flex flex-col gap-2 lg:flex-row lg:items-end"
            onFocus={() => setGroutActive("joint")} onBlur={() => setGroutActive((k) => (k === "joint" ? null : k))}>
            <label className="flex min-w-0 flex-1 flex-col gap-1">
              <span className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">{t("stratGroutTitle")}</span>
              <select data-testid="grout-select" value={groutId || ""} onChange={(e) => setGroutId(e.target.value)}
                className="h-10 w-full border border-input bg-surface px-2 text-sm outline-none focus:ring-1 focus:ring-primary">
                {grouts.length === 0 && <option value="">{t("stratNoProduct")}</option>}
                {grouts.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
                <option value={OTHER_ID}>{t("stratOther")}</option>
              </select>
            </label>
            <label className="flex w-full flex-col gap-1 lg:w-[190px]">
              <span className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">{t("stratFieldPriceKg")}</span>
              <div className="flex h-10 items-stretch border border-input bg-surface">
                <span className="flex shrink-0 items-center bg-primary px-2 text-xs font-bold text-primary-foreground">CHF/kg</span>
                <input data-testid="grout-price" type="number" step="0.01" inputMode="decimal" value={gIsOther ? 0 : gShownKg} disabled={gIsOther}
                  onChange={(e) => groutId && setOverride((o) => ({ ...o, [groutId]: e.target.value }))}
                  className="w-full bg-transparent px-2 text-right font-mono text-sm outline-none disabled:text-muted-foreground" />
              </div>
            </label>
          </div>
          {groutInTotal && (
            <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 border-t border-border pt-2 font-mono text-xs text-muted-foreground">
              {gIsOther ? (
                <span className="text-muted-foreground/80">{t("stratOther")}</span>
              ) : (
                <>
                  <span>{gTotalKg.toFixed(1)} {t("stratKgNeeded")}</span>
                  <span className="flex items-center gap-1 font-semibold text-foreground"><Package className="h-3 w-3" /> {gBags || "—"} × {gBag}kg</span>
                  {gResidual > 0.05 && <span className="text-amber-500" data-testid="grout-residual">{t("stratLeftover")} {gResidual.toFixed(2)} kg</span>}
                  <span className="text-muted-foreground/70">{gKgM2.toFixed(2)} kg/m²</span>
                </>
              )}
              <span className="ml-auto text-sm font-bold text-foreground" data-testid="grout-subtotal">{chf(gSubtotal)}</span>
            </div>
          )}
        </div>
        <LayerSelect layerKey="colla" {...layerCtx} list={[...collas, makeOther(t("stratOther"))]} selectedId={sel.colla} thk={collaThk} calc={cColla} editableThk onThk={setCollaThk} note={`4 · ${t("stratColla")}`} />

        <AddLayerButton active={precollaOn} onToggle={() => setPrecollaOn(!precollaOn)}
          labelAdd={t("stratAddPrecolla")} labelRemove={t("stratRmPrecolla")} testid="toggle-precolla" />
        {precollaOn && (
          <LayerSelect layerKey="precolla" {...layerCtx} list={[...primers, makeOther(t("stratOther"))]} selectedId={sel.precolla} thk={0} calc={cPrecolla} editableThk={false} note={`3 · ${t("stratPrecolla")} ${t("stratOptional")}`} />
        )}

        <LayerSelect layerKey="base" {...layerCtx} list={[...bases, makeOther(t("stratOther"))]} selectedId={sel.base} thk={baseThk} calc={cBase} editableThk={false} note={`2 · ${baseSub === "massetti" ? t("stratMassetto") : t("stratAutolivellante")} ${t("stratThkCalc")}`} />

        {/* heating pipes */}
        <div data-testid="heating-panel"
          onMouseEnter={() => heatingOn && setActiveK("heating")} onMouseLeave={() => setActiveK((k) => (k === "heating" ? null : k))}
          onFocusCapture={() => heatingOn && setActiveK("heating")} onBlurCapture={() => setActiveK((k) => (k === "heating" ? null : k))}>
          <AddLayerButton active={heatingOn} onToggle={() => setHeatingOn(!heatingOn)}
            labelAdd={t("stratAddHeating")} labelRemove={t("stratRmHeating")} testid="toggle-heating" />
          {heatingOn && (
            <div className="mt-3 border border-border bg-card p-4" style={activeK === "heating" ? { boxShadow: "inset 0 0 0 2px #f59e0b" } : undefined}>
              <div className="flex flex-wrap gap-3">
                <label className="flex flex-col gap-1">
                  <span className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">{t("stratPipeDia")}</span>
                  <input data-testid="input-pipedia" type="number" inputMode="decimal" value={pipeDia} onChange={(e) => setPipeDia(e.target.value)}
                    className="h-10 w-28 border border-input bg-surface px-2 text-right font-mono text-sm outline-none focus:ring-1 focus:ring-primary" />
                </label>
                <label className="flex flex-col gap-1">
                  <span className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">{t("stratPipeSpacing")}</span>
                  <input data-testid="input-pipespacing" type="number" inputMode="decimal" value={pipeSpacing} onChange={(e) => setPipeSpacing(e.target.value)}
                    className="h-10 w-28 border border-input bg-surface px-2 text-right font-mono text-sm outline-none focus:ring-1 focus:ring-primary" />
                </label>
                <div className="flex flex-col justify-end">
                  <span className="font-mono text-xs text-emerald-600">−{pipeEquivMm.toFixed(1)} mm {t("stratLeveler")} / m²</span>
                </div>
              </div>
            </div>
          )}
        </div>

        <AddLayerButton active={primerOn} onToggle={() => setPrimerOn(!primerOn)}
          labelAdd={t("stratAddPrimer")} labelRemove={t("stratRmPrimer")} testid="toggle-primer" />
        {primerOn && (
          <LayerSelect layerKey="primer" {...layerCtx} list={[...primers, makeOther(t("stratOther"))]} selectedId={sel.primer} thk={primerThk} calc={cPrimer} editableThk onThk={setPrimerThk} note={`1 · ${t("stratLayerPrimer")}`} />
        )}
        {analysis && (
          <div className="flex items-start gap-2 border border-sky-400 bg-sky-400/10 px-3 py-2 text-xs" data-testid="photo-analysis">
            <Info className="mt-0.5 h-4 w-4 shrink-0 text-sky-400" />
            <span><b>{t("stratSubstrateLbl")}</b> {analysis.substrate || "—"} · <b>{t("stratPrimerRecommended")}</b> {analysis.primer_type} ({analysis.necessity}). {analysis.note}</span>
          </div>
        )}

        {/* photo */}
        <div className="border border-border bg-card p-4">
          <div className="mb-2 flex items-center gap-2 label-caps text-muted-foreground"><Camera className="h-4 w-4" /> {t("stratPhotoTitle")}</div>
          {photo ? (
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
              <img src={photo.url} alt="substrate" className="h-24 w-40 shrink-0 border border-border object-cover" />
              <div className="flex flex-wrap gap-2">
                <button onClick={runAnalysis} disabled={analyzing} data-testid="analyze-photo"
                  className="flex items-center justify-center gap-2 border border-primary bg-primary/10 px-4 py-2 text-sm font-semibold text-primary disabled:opacity-60">
                  {analyzing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Info className="h-4 w-4" />}
                  {analyzing ? t("stratAnalyzing") : t("stratAnalyze")}
                </button>
                <button onClick={() => fileRef.current?.click()} data-testid="change-photo"
                  className="flex items-center gap-2 border border-border px-3 py-2 text-sm font-semibold hover:border-primary">
                  <Camera className="h-4 w-4" /> {t("stratChange")}
                </button>
                <button onClick={removePhoto} data-testid="remove-photo"
                  className="flex items-center gap-2 border border-border px-3 py-2 text-sm font-semibold text-red-500 hover:border-red-500">
                  <X className="h-4 w-4" /> {t("stratRemove")}
                </button>
              </div>
            </div>
          ) : (
            <button onClick={() => fileRef.current?.click()} data-testid="upload-photo"
              className="flex h-20 w-full items-center justify-center gap-2 border border-dashed border-border text-sm text-muted-foreground hover:border-primary">
              <Camera className="h-5 w-5" /> {t("stratUploadPhoto")}
            </button>
          )}
          <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onPhoto} />
        </div>

        <div className="flex flex-wrap items-center gap-3 border border-primary bg-primary/5 px-4 py-3" data-testid="system-total">
          <span className="font-display text-lg font-black">{t("stratTotal")}</span>
          <span className="ml-auto font-mono text-xl font-black text-primary">{chf(grandTotal)}</span>
          <button data-testid="generate-pdf" onClick={() => genPDF()}
            className="flex items-center gap-2 bg-primary px-4 py-2.5 text-sm font-bold text-primary-foreground hover:opacity-90">
            <FileDown className="h-4 w-4" /> {t("stratGenPDF")}
          </button>
        </div>
      </div>
      </div>

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-lg" data-testid="datasheet-dialog">
          {detail && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 font-display">
                  <FileText className="h-5 w-5 text-primary" /> {(detail.name || "").toLowerCase().startsWith((detail.brand || "").toLowerCase()) ? detail.name : `${detail.brand} ${detail.name}`}
                </DialogTitle>
                <DialogDescription>{detail.description || t("stratDatasheetDefault")}</DialogDescription>
              </DialogHeader>
              <div className="mt-2 grid grid-cols-2 gap-x-4 gap-y-2 text-sm" data-testid="datasheet-specs">
                {datasheetRows(detail).map(([k, v]) => (
                  <div key={k} className="flex flex-col border-b border-border/60 pb-1">
                    <span className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">{k}</span>
                    <span className="font-mono">{v}</span>
                  </div>
                ))}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
